// src/lib/ReactQueryDataProvider.jsx

import { createContext, useContext, useMemo, useCallback, useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useReactQueryData } from '../hooks/useReactQueryData'
import { useSupabaseAuth } from './useSupabaseAuth.jsx'
import { queryClient } from './query-client'
import { supabase, getCompanyId } from './supabase'
import { RealtimeManager } from './realtime'
import { toContextAccessor } from './cache/entityCache'
import {
  useCalendarEventsQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
} from './cache/calendarEvents'
import {
  useCompaniesQuery,
  useUpdateCompanyMutation,
} from './cache/companies'
import {
  useClientsQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
} from './cache/clients'
import {
  useEstimatesQuery,
  useCreateEstimateMutation,
  useUpdateEstimateMutation,
  useDeleteEstimateMutation,
} from './cache/estimates'
import {
  useInvoicesQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
} from './cache/invoices'
import {
  useJobsQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  useDeleteJobMutation,
} from './cache/jobs'
import {
  usePaymentsQuery,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useDeletePaymentMutation,
} from './cache/payments'
import {
  useExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
} from './cache/expenses'
import {
  useEmployeesQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
} from './cache/employees'
import {
  useExpenseCategoriesQuery,
  useCreateExpenseCategoryMutation,
  useUpdateExpenseCategoryMutation,
  useDeleteExpenseCategoryMutation,
} from './cache/expenseCategories'
import {
  useRecurringExpensesQuery,
  useCreateRecurringExpenseMutation,
  useUpdateRecurringExpenseMutation,
  useDeleteRecurringExpenseMutation,
} from './cache/recurringExpenses'

const ReactQueryDataContext = createContext()

export const useReactQueryDataContext = () => {
  const context = useContext(ReactQueryDataContext)
  if (!context) {
    // Return safe defaults instead of throwing to prevent hooks violations
    // This allows components to render while context is initializing
    console.warn('useReactQueryDataContext called outside provider, using defaults')
    return {
      employees: { data: [], isLoading: true, error: null },
      jobs: { data: [], isLoading: true, error: null },
      clients: { data: [], isLoading: true, error: null },
      estimates: { data: [], isLoading: true, error: null },
      invoices: { data: [], isLoading: true, error: null },
      loading: true,
      error: null,
      refreshData: () => Promise.resolve(),
      deleteEmployee: async () => ({ success: false, error: 'Context not ready' }),
      getData: () => ({ data: [], isLoading: true, isLoaded: false, error: null }),
      loadedData: {},
      loadingState: { isInitializing: true, isBackgroundRefreshing: false },
      performanceMetrics: {}
    }
  }
  return context
}

// Helper functions for time-based optimization (same as OptimizedDataProvider)
const getTimeOfDay = () => {
  const hour = new Date().getHours()
  if (hour >= 6 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 17) return 'afternoon'
  return 'evening'
}

const getCurrentSeason = () => {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 'spring'
  if (month >= 5 && month <= 7) return 'summer'
  if (month >= 8 && month <= 10) return 'fall'
  return 'winter'
}

// Data Provider Component
const ReactQueryDataProviderInner = ({ children }) => {
  const { user, companyId, authReady } = useSupabaseAuth()
  
  // Per-entity caches (Phase A migration; see lib/cache/*).
  // Each one replaces a slice of the mega-query for that entity.
  const calendarEventsQuery = useCalendarEventsQuery()
  const createCalendarEventMutation = useCreateCalendarEventMutation()
  const updateCalendarEventMutation = useUpdateCalendarEventMutation()
  const deleteCalendarEventMutation = useDeleteCalendarEventMutation()

  const clientsQuery = useClientsQuery()
  const createClientMutation = useCreateClientMutation()
  const updateClientMutation = useUpdateClientMutation()
  const deleteClientMutation = useDeleteClientMutation()

  const estimatesQuery = useEstimatesQuery()
  const createEstimateMutation = useCreateEstimateMutation()
  const updateEstimateMutation = useUpdateEstimateMutation()
  const deleteEstimateMutation = useDeleteEstimateMutation()

  const invoicesQuery = useInvoicesQuery()
  const createInvoiceMutation = useCreateInvoiceMutation()
  const updateInvoiceMutation = useUpdateInvoiceMutation()
  const deleteInvoiceMutation = useDeleteInvoiceMutation()

  const jobsQuery = useJobsQuery()
  const createJobMutation = useCreateJobMutation()
  const updateJobMutation = useUpdateJobMutation()
  const deleteJobMutation = useDeleteJobMutation()

  const companiesQuery = useCompaniesQuery()
  const updateCompanyMutation = useUpdateCompanyMutation()

  const paymentsQuery = usePaymentsQuery()
  const createPaymentMutation = useCreatePaymentMutation()
  const updatePaymentMutation = useUpdatePaymentMutation()
  const deletePaymentMutation = useDeletePaymentMutation()

  const expensesQuery = useExpensesQuery()
  const createExpenseMutation = useCreateExpenseMutation()
  const updateExpenseMutation = useUpdateExpenseMutation()
  const deleteExpenseMutation = useDeleteExpenseMutation()

  const employeesQuery = useEmployeesQuery()
  const createEmployeeMutation = useCreateEmployeeMutation()
  const updateEmployeeMutation = useUpdateEmployeeMutation()
  const deleteEmployeeMutation = useDeleteEmployeeMutation()

  const expenseCategoriesQuery = useExpenseCategoriesQuery()
  const createExpenseCategoryMutation = useCreateExpenseCategoryMutation()
  const updateExpenseCategoryMutation = useUpdateExpenseCategoryMutation()
  const deleteExpenseCategoryMutation = useDeleteExpenseCategoryMutation()

  const recurringExpensesQuery = useRecurringExpensesQuery()
  const createRecurringExpenseMutation = useCreateRecurringExpenseMutation()
  const updateRecurringExpenseMutation = useUpdateRecurringExpenseMutation()
  const deleteRecurringExpenseMutation = useDeleteRecurringExpenseMutation()

  const {
    loadedData,
    loadingState,
    refreshData,
    getData,
    performanceMetrics,
    warmCache,
    queryClient,
  } = useReactQueryData(
    companyId,
    {
      role: user?.user_metadata?.role || 'owner',
      timeOfDay: getTimeOfDay(),
      season: getCurrentSeason(),
    },
    authReady,
    // Boot-set query loading flags — splash gate waits for all of these.
    [clientsQuery.isLoading, companiesQuery.isLoading]
  )

  // Initialize Centralized Realtime Manager
  // Handles all realtime subscriptions, visibility/focus events, and connection lifecycle
  useEffect(() => {
    if (companyId && authReady) {
      RealtimeManager.initialize(supabase, companyId, queryClient)
    }

    return () => {
      RealtimeManager.destroy()
    }
  }, [companyId, authReady])

  // Subscribe the single user_access_cache realtime channel. Drives
  // invalidation of ['userAccessCache', userId] so every usePermissions
  // consumer reacts to admin permission updates without each one mounting
  // its own channel. Must run after initialize() (the manager owns the
  // supabase ref) — the effect ordering above guarantees that.
  useEffect(() => {
    if (user?.id && authReady && companyId) {
      RealtimeManager.subscribeToUserAccess(user.id)
    }
  }, [user?.id, authReady, companyId])

  // PERFORMANCE FIX: Memoize createEntityAccessor to prevent recreation on every render
  // This ensures the useMemo below only recalculates when getData actually changes
  // Prevents unnecessary re-renders of all data consumers
  const createEntityAccessor = useCallback((entityName) => {
    const entityData = getData(entityName)
    return {
      data: entityData.data || [],
      loading: entityData.isLoading,
      error: entityData.error,
      isLoaded: entityData.isLoaded
    }
  }, [getData]) // Only recreate if getData changes

  const value = useMemo(() => ({
    // Core data access
    data: loadedData,
    loading: loadingState.isInitializing,
    error: loadingState.errors[0] || null,
    companyId,
    user,
    
    // Enhanced loading state for better rendering control
    isDataReady: !loadingState.isInitializing,
    isInitializing: loadingState.isInitializing,
    
    // Actions
    refreshData,
    getData,
    warmCache,
    
    // Performance
    performanceMetrics,
    loadingState,
    
    // ALL 50+ Entities with backward compatibility (same as OptimizedDataProvider)
    // Core Business Entities
    companies: toContextAccessor(companiesQuery),
    clients: toContextAccessor(clientsQuery),
    services: createEntityAccessor('services'),
    packages: createEntityAccessor('packages'),
    service_categories: createEntityAccessor('service_categories'),
    
    // Job Management
    jobs: toContextAccessor(jobsQuery),
    job_activity: createEntityAccessor('job_activity'),
    job_checklists: createEntityAccessor('job_checklists'),
    job_checklist_items: createEntityAccessor('job_checklist_items'),
    job_media: createEntityAccessor('job_media'),
    job_checklist_attachments: createEntityAccessor('job_checklist_attachments'),
    
    // Financial Management
    estimates: toContextAccessor(estimatesQuery),
    invoices: toContextAccessor(invoicesQuery),
    payments: toContextAccessor(paymentsQuery),
    expenses: toContextAccessor(expensesQuery),
    expense_categories: toContextAccessor(expenseCategoriesQuery),
    recurring_expenses: toContextAccessor(recurringExpensesQuery),
    expense_receipts: createEntityAccessor('expense_receipts'),
    
    // Team Management
    employees: toContextAccessor(employeesQuery),
    employee_permissions: createEntityAccessor('employee_permissions'),
    teams: createEntityAccessor('teams'),
    team_tiers: createEntityAccessor('team_tiers'),
    team_tier_members: createEntityAccessor('team_tier_members'),
    job_assignments: createEntityAccessor('job_assignments'),
    time_off_requests: createEntityAccessor('time_off_requests'),
    
    // Communication & Messaging
    messages: createEntityAccessor('messages'),
    message_logs: createEntityAccessor('message_logs'),
    email_logs: createEntityAccessor('email_logs'),
    calendar_events: toContextAccessor(calendarEventsQuery),
    
    // Automation & Workflow
    automations: createEntityAccessor('automations'),
    automation_templates: createEntityAccessor('automation_templates'),
    automation_executions: createEntityAccessor('automation_executions'),
    scheduled_automation_queue: createEntityAccessor('scheduled_automation_queue'),
    
    // Public Access & Magic Links
    magic_links: createEntityAccessor('magic_links'),
    client_responses: createEntityAccessor('client_responses'),
    
    // AI & RAG
    ai_conversations: createEntityAccessor('ai_conversations'),
    ai_messages: createEntityAccessor('ai_messages'),
    rag_documents: createEntityAccessor('rag_documents'),
    rag_chunks: createEntityAccessor('rag_chunks'),
    
    // File Management
    files: createEntityAccessor('files'),
    
    // Task Management
    tasks: createEntityAccessor('tasks'),
    
    // Vendor Management
    vendors: createEntityAccessor('vendors'),
    
    // System & Configuration
    company_branding: createEntityAccessor('company_branding'),
    user_dashboard_preferences: createEntityAccessor('user_dashboard_preferences'),
    user_toolbelt_preferences: createEntityAccessor('user_toolbelt_preferences'),
    user_access_cache: createEntityAccessor('user_access_cache'),
    
    // Audit & Logging
    audit_logs: createEntityAccessor('audit_logs'),
    
    // Integration
    webhook_events: createEntityAccessor('webhook_events'),
    
    // Computed Data Views
    job_financial_summary: createEntityAccessor('job_financial_summary'),
    job_costs_by_category: createEntityAccessor('job_costs_by_category'),
    estimate_budget_by_category: createEntityAccessor('estimate_budget_by_category'),
    job_checklist_complete: createEntityAccessor('job_checklist_complete'),
    
    // Backward compatibility with old DataProvider interface
    calendarEvents: toContextAccessor(calendarEventsQuery),
    
    // Patch a single entity list inside the essential-data cache. Use this
    // instead of awaiting refreshData('entity') so saves return as soon as
    // Supabase confirms, without blocking on a full company-wide refetch.
    patchEntityCache: (entityName, updater) => {
      queryClient.setQueryData(['allCompanyData', companyId], (oldData) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          [entityName]: updater(oldData[entityName] || [])
        }
      })
    },

    // Client methods — backed by per-entity cache (lib/cache/clients.js).
    // Optimistic apply on mutate; reconcile from server row on success; rollback
    // + invalidate on error. No mega-query patching.
    addClient: async (clientData) => {
      return createClientMutation.mutateAsync(clientData)
    },

    updateClient: async (clientId, clientData) => {
      return updateClientMutation.mutateAsync({ id: clientId, ...clientData })
    },

    deleteClient: async (clientId) => {
      return deleteClientMutation.mutateAsync(clientId)
    },

    // Payment methods — backed by per-entity cache (lib/cache/payments.js).
    addPayment: async (paymentData) => createPaymentMutation.mutateAsync(paymentData),
    updatePayment: async (paymentId, paymentData) =>
      updatePaymentMutation.mutateAsync({ id: paymentId, ...paymentData }),
    deletePayment: async (paymentId) => deletePaymentMutation.mutateAsync(paymentId),

    // Expense methods — backed by per-entity cache (lib/cache/expenses.js).
    addExpense: async (expenseData) => createExpenseMutation.mutateAsync(expenseData),
    updateExpense: async (expenseId, expenseData) =>
      updateExpenseMutation.mutateAsync({ id: expenseId, ...expenseData }),
    deleteExpense: async (expenseId) => deleteExpenseMutation.mutateAsync(expenseId),

    // Job methods — backed by per-entity cache (lib/cache/jobs.js).
    // Optimistic apply on mutate; reconcile from server row on success;
    // rollback + invalidate on error. No mega-query patching, no
    // refreshData round trip. Realtime fan-out is also wired in
    // RealtimeManager so writes from other tabs / from the public job
    // surface land in the same cache.
    //
    // Push notifications fire fire-and-forget to every user with the
    // 'jobs' permission on create / status='completed' transitions, minus
    // the acting user (no point pushing your own action to your phone).
    // Backed by the get_users_with_permission RPC; see lib/permissionPush.
    addJob: async (jobData) => {
      const result = await createJobMutation.mutateAsync(jobData)
      try {
        if (companyId && result?.id) {
          const { notifyUsersWithPermission } = await import('./permissionPush')
          notifyUsersWithPermission({
            companyId,
            permissionName: 'jobs',
            excludeUserId: user?.id,
            payload: {
              eventType: 'job_created',
              title: 'New Job Created',
              body: `Job "${result.title || result.id}" has been created`,
              url: `/jobs/${result.id}`,
              priority: 'normal',
              relatedEntityType: 'job',
              relatedEntityId: result.id,
            },
          })
        }
      } catch (err) {
        console.error('[ReactQueryDataProvider] job_created push setup failed:', err)
      }
      return result
    },

    updateJob: async (jobId, jobData) => {
      const result = await updateJobMutation.mutateAsync({ id: jobId, ...jobData })
      try {
        if (jobData?.status === 'completed' && companyId && result?.id) {
          const { notifyUsersWithPermission } = await import('./permissionPush')
          notifyUsersWithPermission({
            companyId,
            permissionName: 'jobs',
            excludeUserId: user?.id,
            payload: {
              eventType: 'job_completed',
              title: '✅ Job Completed',
              body: `Job "${result.title || result.id}" has been marked as completed`,
              url: `/jobs/${result.id}`,
              priority: 'normal',
              relatedEntityType: 'job',
              relatedEntityId: result.id,
            },
          })
        }
      } catch (err) {
        console.error('[ReactQueryDataProvider] job_completed push setup failed:', err)
      }
      return result
    },

    deleteJob: async (jobId) => {
      return deleteJobMutation.mutateAsync(jobId)
    },

    // Company patch — optimistic update against the per-entity cache.
    // Prefer this over direct `supabase.from('companies').update(...)` +
    // `refreshData('companies')` so settings saves return as soon as
    // Supabase confirms without a full refetch round trip.
    updateCompany: async (companyId, patch) => {
      return updateCompanyMutation.mutateAsync({ id: companyId, ...patch })
    },
    
    // Employee methods — backed by per-entity cache (lib/cache/employees.js).
    // Create uses useRefetchListMutation because the server slot-limit
    // trigger can reject inserts in ways the client can't model; refetch
    // after success picks up the real row. Update/delete are optimistic.
    addEmployee: async (employeeData) =>
      createEmployeeMutation.mutateAsync(employeeData),
    updateEmployee: async (employeeId, employeeData) =>
      updateEmployeeMutation.mutateAsync({ id: employeeId, ...employeeData }),
    deleteEmployee: async (employeeId) =>
      deleteEmployeeMutation.mutateAsync(employeeId),
    
    // Estimate methods — backed by per-entity cache (lib/cache/estimates.js).
    // Create uses the deferredField hybrid: optimistic row appears instantly
    // with every safe field populated but estimate_number left as a sentinel
    // ('…'); the mutationFn calls the next_estimate_number RPC inside the
    // server round trip and reconcile replaces the placeholder with the full
    // server row (including the real number and the joined client object).
    // Callers must NOT pass estimate_number — the factory throws in dev if
    // they do.
    addEstimate: async (estimateData) => {
      return createEstimateMutation.mutateAsync(estimateData)
    },

    updateEstimate: async (estimateId, estimateData) => {
      return updateEstimateMutation.mutateAsync({ id: estimateId, ...estimateData })
    },

    deleteEstimate: async (estimateId) => {
      return deleteEstimateMutation.mutateAsync(estimateId)
    },
    
    // Invoice methods — backed by per-entity cache (lib/cache/invoices.js).
    // Create uses the deferredField hybrid: optimistic row appears instantly
    // with every safe field populated but invoice_number left as a sentinel
    // ('…'); the mutationFn lets the createInvoice API fabricate the real
    // number and reconcile replaces the placeholder with the full server row.
    // Callers must NOT pass invoice_number — the factory strips it.
    addInvoice: async (invoiceData) => {
      return createInvoiceMutation.mutateAsync(invoiceData)
    },

    updateInvoice: async (invoiceId, invoiceData) => {
      return updateInvoiceMutation.mutateAsync({ id: invoiceId, ...invoiceData })
    },

    deleteInvoice: async (invoiceId) => {
      return deleteInvoiceMutation.mutateAsync(invoiceId)
    },
    
    // Calendar event methods — backed by per-entity cache (lib/cache/calendarEvents.js).
    // Optimistic apply on mutate; reconcile from server row on success; rollback
    // + invalidate on error. No mega-query patching, no refreshData round trip.
    addCalendarEvent: async (eventData) => {
      return createCalendarEventMutation.mutateAsync(eventData)
    },

    updateCalendarEvent: async (eventId, eventData) => {
      return updateCalendarEventMutation.mutateAsync({ id: eventId, ...eventData })
    },

    deleteCalendarEvent: async (eventId) => {
      return deleteCalendarEventMutation.mutateAsync(eventId)
    },

    // AI Conversation methods
    updateConversation: async (conversationId, updates) => {
      const { supabase } = await import('./supabase')
      const result = await supabase
        .from('ai_conversations')
        .update(updates)
        .eq('id', conversationId)
      
      if (result?.data) {
        await refreshData('ai_conversations')
      }
      return result
    },

    deleteConversation: async (conversationId) => {
      const { supabase } = await import('./supabase')
      const result = await supabase
        .from('ai_conversations')
        .delete()
        .eq('id', conversationId)

      if (result?.error) {
        throw result.error
      }
      queryClient.setQueryData(['allCompanyData', companyId], (oldData) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          ai_conversations: (oldData.ai_conversations || []).filter(c => c.id !== conversationId)
        }
      })
      return result
    },
    
    // Implemented methods for actual usage
    
    deleteTemplate: async (templateId) => {
      try {
        const { deleteAutomationTemplate } = await import('./api')
        
        const result = await deleteAutomationTemplate(templateId)

        if (result?.data !== undefined) {
          // Fire-and-forget: automation_templates isn't in the essential cache,
          // so we can't patch it with setQueryData. Don't await — let the save
          // return immediately and the refresh happen in the background.
          refreshData('automation_templates').catch(err => {
            console.error('[ReactQueryDataProvider] deleteTemplate refresh failed:', err)
          })
        }

        return result
      } catch (error) {
        console.error('[ReactQueryDataProvider] deleteTemplate failed:', error)
        throw error
      }
    },
    
    setAutomations: async (automationData) => {
      try {
        const { upsertAutomation } = await import('./api')
        const companyId = await getCompanyId()
        
        if (!companyId) {
          throw new Error('Company ID not found')
        }

        const result = await upsertAutomation({
          ...automationData,
          company_id: companyId
        })

        if (result?.data) {
          // Fire-and-forget: automations lives in the backgroundQuery cache,
          // not essentialQuery, so we can't patch via setQueryData on
          // ['allCompanyData', companyId]. Don't await the refresh.
          refreshData('automations').catch(err => {
            console.error('[ReactQueryDataProvider] setAutomations refresh failed:', err)
          })
        }

        return result
      } catch (error) {
        console.error('[ReactQueryDataProvider] setAutomations failed:', error)
        throw error
      }
    },
    
    
    // Legacy properties for backward compatibility
    initialized: !loadingState.isInitializing,
    communications: [],
    consentLogs: [],
    templates: [],
    smsSettings: {
      from: '+15551234567',
      quietHours: {
        start: '22:00',
        end: '08:00'
      }
    },
    serviceCenter: {},
    integrations: [],
    reports: [],
    scheduling: [],
    inspection: [],
    selfService: {},
    publicEstimate: {},
    paymentDashboard: {},
    itemLibrary: {},
    automationsSettings: {},
    availability: []
  }), [
    loadedData,
    loadingState,
    companyId,
    user,
    refreshData,
    getData,
    warmCache,
    performanceMetrics,
    createEntityAccessor,
    calendarEventsQuery,
    createCalendarEventMutation,
    updateCalendarEventMutation,
    deleteCalendarEventMutation,
    clientsQuery,
    createClientMutation,
    updateClientMutation,
    deleteClientMutation,
    estimatesQuery,
    createEstimateMutation,
    updateEstimateMutation,
    deleteEstimateMutation,
    invoicesQuery,
    createInvoiceMutation,
    updateInvoiceMutation,
    deleteInvoiceMutation,
    jobsQuery,
    createJobMutation,
    updateJobMutation,
    deleteJobMutation,
    companiesQuery,
    updateCompanyMutation,
    paymentsQuery,
    createPaymentMutation,
    updatePaymentMutation,
    deletePaymentMutation,
    expensesQuery,
    createExpenseMutation,
    updateExpenseMutation,
    deleteExpenseMutation,
    employeesQuery,
    createEmployeeMutation,
    updateEmployeeMutation,
    deleteEmployeeMutation,
    expenseCategoriesQuery,
    createExpenseCategoryMutation,
    updateExpenseCategoryMutation,
    deleteExpenseCategoryMutation,
    recurringExpensesQuery,
    createRecurringExpenseMutation,
    updateRecurringExpenseMutation,
    deleteRecurringExpenseMutation,
  ])

  return (
    <ReactQueryDataContext.Provider value={value}>
      {children}
    </ReactQueryDataContext.Provider>
  )
}

// Main Provider Component with QueryClient
export const ReactQueryDataProvider = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDataProviderInner>
        {children}
      </ReactQueryDataProviderInner>
      {/* React Query DevTools - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools 
          initialIsOpen={false}
          position="bottom-right"
        />
      )}
    </QueryClientProvider>
  )
}

// Export the context for advanced usage
export { ReactQueryDataContext }
